// Music Gene Entertainment — Warm Soul behaviours for the mge-* sections.

// Hero "Hear the sounds" button: plays the front cover's preview through the bottom player.
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-mge-play-lead]');
  if (!trigger) return;
  const previewButton = trigger.closest('.mge-hero')?.querySelector('.mge-hero__cover-play product-audio-preview button');
  if (previewButton) previewButton.click();
});

// Sticky add-to-cart bar (snippets/mge-sticky-atc.liquid). It mirrors and clicks the real product form
// button, so variants, the cart drawer and sold-out states keep using the theme's own logic.
if (!customElements.get('mge-sticky-atc')) {
  class MgeStickyAtc extends HTMLElement {
    connectedCallback() {
      // Quick Look copies the product section into a modal; the bar is only for the full page.
      if (this.closest('quick-add-modal')) {
        this.remove();
        return;
      }

      this.sectionId = this.dataset.section;
      this.button = this.querySelector('[data-mge-sticky-submit]');
      this.priceElement = this.querySelector('[data-mge-sticky-price]');
      if (!this.submitButton || !this.button) return;

      this.abortController = new AbortController();
      this.button.addEventListener('click', () => this.submitButton?.click(), { signal: this.abortController.signal });

      // Scroll check (rAF-throttled) instead of IntersectionObserver: fast flings and anchor jumps can move
      // the button from below to above the viewport without crossing an observer threshold.
      const update = () => {
        this.frame = null;
        const rect = this.submitButton?.getBoundingClientRect();
        this.setVisible(Boolean(rect && rect.height && rect.bottom < 0));
      };
      const schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(update);
      };
      window.addEventListener('scroll', schedule, { passive: true, signal: this.abortController.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this.abortController.signal });
      update();

      this.mutationObserver = new MutationObserver(() => this.sync());
      this.mutationObserver.observe(this.submitButton, { attributes: true, childList: true, subtree: true, characterData: true });
      const price = document.getElementById(`price-${this.sectionId}`);
      if (price) this.mutationObserver.observe(price, { childList: true, subtree: true });
      this.sync();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.mutationObserver?.disconnect();
      document.body.classList.remove('mge-sticky-atc-open');
    }

    get submitButton() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    setVisible(visible) {
      if (visible) this.hidden = false;
      this.classList.toggle('is-visible', visible);
      document.body.classList.toggle('mge-sticky-atc-open', visible);
    }

    sync() {
      const submit = this.submitButton;
      if (!submit) return;

      this.button.disabled = submit.disabled || submit.getAttribute('aria-disabled') === 'true';
      const label = (submit.querySelector('span') || submit).textContent.trim();
      if (label) this.button.textContent = label;

      const price = document.getElementById(`price-${this.sectionId}`)?.querySelector('.price');
      if (!price || !this.priceElement) return;
      const current = price.classList.contains('price--on-sale')
        ? price.querySelector('.price__sale .price-item--sale')
        : price.querySelector('.price__regular .price-item--regular');
      const text = current?.textContent.trim();
      if (text) this.priceElement.textContent = text;
    }
  }

  customElements.define('mge-sticky-atc', MgeStickyAtc);
}

// Collection toolbar (sections/mge-collection.liquid): genre chips + instant search filter the cards
// already on the page; the sort select reloads the collection with ?sort_by=.
if (!customElements.get('mge-collection-filter')) {
  class MgeCollectionFilter extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.mge-collection');
      this.items = Array.from(this.section.querySelectorAll('[data-mge-item]'));
      this.count = this.section.querySelector('[data-mge-count]');
      this.empty = this.section.querySelector('[data-mge-empty]');
      this.search = this.querySelector('[data-mge-search]');
      this.tag = '';
      this.query = '';

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.querySelectorAll('[data-mge-filter]').forEach((chip) => {
        chip.addEventListener('click', () => this.selectTag(chip), { signal });
      });

      if (this.search) {
        let timer;
        this.search.addEventListener(
          'input',
          () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
              this.query = this.search.value.trim().toLowerCase();
              this.apply();
            }, 120);
          },
          { signal }
        );
      }

      this.section.querySelector('[data-mge-reset]')?.addEventListener('click', () => this.reset(), { signal });

      const sort = this.querySelector('[data-mge-sort]');
      sort?.addEventListener('change', () => sort.form.submit(), { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    selectTag(chip) {
      this.tag = chip.dataset.mgeFilter;
      this.querySelectorAll('[data-mge-filter]').forEach((other) => {
        const active = other === chip;
        other.classList.toggle('is-active', active);
        other.setAttribute('aria-pressed', active);
      });
      this.apply();
    }

    reset() {
      if (this.search) this.search.value = '';
      this.query = '';
      const all = this.querySelector('[data-mge-filter=""]');
      if (all) this.selectTag(all);
      else this.apply();
    }

    apply() {
      let visible = 0;
      this.items.forEach((item) => {
        const tags = (item.dataset.tags || '').split('|');
        const matchesTag = !this.tag || tags.includes(this.tag);
        const matchesQuery = !this.query || (item.dataset.search || '').includes(this.query);
        const show = matchesTag && matchesQuery;
        item.hidden = !show;
        if (show) visible++;
      });
      if (this.count) this.count.textContent = visible;
      if (this.empty) this.empty.hidden = visible > 0;
    }
  }

  customElements.define('mge-collection-filter', MgeCollectionFilter);
}
